import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { RewardOption, RewardOptionGroup } from '@/components/admin/mail/reward-options';
import type { RewardRow } from '@/components/admin/mail/serialize';

const GROUP_LABELS: Record<RewardOptionGroup, string> = {
  currency: 'Currency / Ticket / Consumable',
  special: 'Special',
  pet: 'Pet',
  character: 'Character',
  skin: 'Skin',
};
const GROUP_ORDER: RewardOptionGroup[] = ['currency', 'special', 'pet', 'character', 'skin'];

interface MailRewardPickerProps {
  options: RewardOption[];
  rows: RewardRow[];
  onChange: (rows: RewardRow[]) => void;
  disabled?: boolean;
  /** 옵션 목록에 없는 delivery key 를 직접 입력할 수 있게 한다(결제 이벤트 티어용). */
  allowCustomKey?: boolean;
}

export function MailRewardPicker({
  options,
  rows,
  onChange,
  disabled,
  allowCustomKey,
}: MailRewardPickerProps) {
  const byKey = useMemo(() => new Map(options.map((o) => [o.key, o])), [options]);
  const grouped = useMemo(() => {
    const m = new Map<RewardOptionGroup, RewardOption[]>();
    for (const o of options) {
      const arr = m.get(o.group) ?? [];
      arr.push(o);
      m.set(o.group, arr);
    }
    return m;
  }, [options]);

  const updateRow = (i: number, patch: Partial<RewardRow>) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const addRow = () => onChange([...rows, { key: '', amount: 1 }]);

  return (
    <div className="flex flex-col gap-2">
      {rows.map((row, i) => {
        const opt = row.key ? byKey.get(row.key) : undefined;
        // 커스텀 여부는 명시 플래그가 1순위다. 저장된 행을 다시 불러온 경우엔 플래그가
        // 없으므로, 옵션에 없는 키(다른 env 의 펫 id 등)를 파생 조건으로 함께 본다.
        // 플래그가 있으면 키를 전부 지워도 입력 필드가 유지된다.
        const isCustom =
          allowCustomKey === true && (row.custom === true || (row.key !== '' && opt === undefined));
        const quantifiable = isCustom || (opt?.quantifiable ?? false);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
          <div key={i} className="flex items-end gap-2">
            <div className="flex-1">
              {isCustom ? (
                <Input
                  placeholder="delivery key 직접 입력"
                  value={row.key}
                  disabled={disabled}
                  // 복붙으로 딸려오는 앞뒤 공백·개행을 즉시 제거한다. 공백이 남으면
                  // 'PET_101 ' 같은 무효 키가 저장되고 중복 검사도 우회된다.
                  onChange={(e) => updateRow(i, { key: e.target.value.trim(), custom: true })}
                />
              ) : (
                <Select
                  value={row.key || undefined}
                  onValueChange={(key) => updateRow(i, { key, amount: 1 })}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reward…" />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUP_ORDER.filter((g) => grouped.has(g)).map((g) => (
                      <div key={g}>
                        <div className="px-2 py-1 text-[10px] font-semibold uppercase text-muted-foreground">
                          {GROUP_LABELS[g]}
                        </div>
                        {grouped.get(g)!.map((o) => (
                          <SelectItem key={o.key} value={o.key}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Input
              type="number"
              min={1}
              className="w-24"
              value={quantifiable ? row.amount : 1}
              disabled={disabled || !quantifiable}
              onChange={(e) => updateRow(i, { amount: Number(e.target.value) || 1 })}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeRow(i)}
              disabled={disabled}
            >
              ✕
            </Button>
          </div>
        );
      })}
      <div className="flex gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={addRow} disabled={disabled}>
          + Add reward
        </Button>
        {allowCustomKey && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            // 빈 키로 시작한다 — 예전의 'CUSTOM_KEY' 플레이스홀더는 비어있지도 않고
            // 수량도 1 이라 검증을 그대로 통과해 지급 불가능한 키로 저장됐다.
            onClick={() => onChange([...rows, { key: '', amount: 1, custom: true }])}
          >
            + 직접 입력
          </Button>
        )}
      </div>
    </div>
  );
}

export default MailRewardPicker;
